import React, { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { useRoutes } from 'react-router-dom';
import Loading from '~~components/Loading/Loading';
import PATH from './PathConstants';
import LoadingPage from './LoadingPage';
import Authentication from './Authentication';

const WaitingComponent = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<LoadingPage />}>
    <Component />
  </Suspense>
);

const EmptyPage = () => (
  <div style={{ color: '#FFF' }}>
    Opps... Something went wrong. Please check your url path again.
  </div>
);
const FullPage = WaitingComponent(lazy(() => import('~~components/FullPage/FullPage')));

const appRoutes: RouteObject[] = [
  {
    path: PATH.LOGIN,
    element: FullPage,
    children: [
      {
        index: true,
        element: WaitingComponent(lazy(() => import('~~modules/Login/Login'))),
      },
    ],
  },
  {
    path: PATH.FORGET_PWD,
    element: FullPage,
    children: [
      {
        index: true,
        element: WaitingComponent(lazy(() => import('~~modules/Login/ResetPassword/ResetPassword'))),
      },
    ],
  },
  {
    path: PATH.MAIN,
    element: WaitingComponent(lazy(() => import('~~components/AppPage/AppPage'))),
    children: [
      {
        path: PATH.USER_PROFILE,
        element: WaitingComponent(lazy(() => import('~~modules/User/UserProfile/UserProfile'))),
      },
      {
        path: PATH.USER_LIST,
        element: WaitingComponent(lazy(() => import('~~modules/User/UserList/UserList'))),
      },
      {
        path: PATH.MACHINE_LIST,
        element: WaitingComponent(lazy(() => import('~~modules/Device/DeviceList'))),
      },
      {
        path: PATH.DEVICE_MODEL_LIST,
        element: WaitingComponent(lazy(() => import('~~modules/DeviceModel/DeviceModelList'))),
      },
      {
        path: PATH.EDIT_DEVICE_MODEL,
        element: WaitingComponent(lazy(() => import('~~modules/DeviceModel/EditDeviceModel/EditDeviceModel'))),
      },
      {
        path: PATH.ANDROID_OTA_LIST,
        element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceOTA'))),
        children: [
          {
            index: true,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceAgent/DeviceAgent'))),
          },
          {
            path: PATH.DEVICE_BSP_OTA,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceBsp/DeviceBsp'))),
          },
          {
            path: PATH.ECB_OTA,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceECB/DeviceECB'))),
          },
          {
            path: PATH.VBB_OTA,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceVBB/DeviceVBB'))),
          },
        ],
      },
      {
        path: PATH.LINUX_OTA_LIST,
        element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/LinuxOTA'))),
        children: [
          {
            index: true,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceBsp/LinuxBsp'))),
          },
          {
            path: PATH.LINUX_ECB_OTA,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceECB/LinuxECB'))),
          },
          {
            path: PATH.LINUX_VBB_OTA,
            element: WaitingComponent(lazy(() => import('~~modules/DeviceOTA/DeviceVBB/LinuxVBB'))),
          },
        ],
      },
      {
        path: PATH.SITE_LIST,
        element: WaitingComponent(lazy(() => import('~~modules/Site/SiteList'))),
      },
      {
        path: PATH.DASHBOARD,
        element: WaitingComponent(lazy(() => import('~~modules/Dashboard/Dashboard'))),
      },
    ],
  },
  {
    path: '*',
    element: FullPage,
    children: [
      {
        path: '*',
        element: <EmptyPage />,
      },
    ],
  },
];

const routes = () => {
  const element = useRoutes(appRoutes);
  return (
    <Authentication>
      {element}
      <Loading />
    </Authentication>
  );
};

export default routes;
